/**
 * Types for the Candidate Follow-Up System
 */

// --- Action Types ---

export enum CandidateFollowUpActionType {
  PHONE_REACHED = 'phone_reached',
  PHONE_UNREACHABLE = 'phone_unreachable',
  WHATSAPP = 'whatsapp',
  EMAIL = 'email',
  TECHNICAL_PROBLEM = 'technical_problem',
  AGENDA_CONFLICT = 'agenda_conflict',
  GENERAL_NOTE = 'general_note',
}

export const FOLLOW_UP_ACTION_TYPE_LABELS: Record<CandidateFollowUpActionType, string> = {
  [CandidateFollowUpActionType.PHONE_REACHED]: 'Telefoon - bereikt',
  [CandidateFollowUpActionType.PHONE_UNREACHABLE]: 'Telefoon - onbereikbaar',
  [CandidateFollowUpActionType.WHATSAPP]: 'WhatsApp',
  [CandidateFollowUpActionType.EMAIL]: 'E-mail',
  [CandidateFollowUpActionType.TECHNICAL_PROBLEM]: 'Technisch probleem',
  [CandidateFollowUpActionType.AGENDA_CONFLICT]: 'Agenda past niet',
  [CandidateFollowUpActionType.GENERAL_NOTE]: 'Algemene notitie',
};

export const FOLLOW_UP_ACTION_TYPE_COLORS: Record<CandidateFollowUpActionType, string> = {
  [CandidateFollowUpActionType.PHONE_REACHED]: 'text-green-600',
  [CandidateFollowUpActionType.PHONE_UNREACHABLE]: 'text-red-500',
  [CandidateFollowUpActionType.WHATSAPP]: 'text-green-500',
  [CandidateFollowUpActionType.EMAIL]: 'text-blue-500',
  [CandidateFollowUpActionType.TECHNICAL_PROBLEM]: 'text-orange-500',
  [CandidateFollowUpActionType.AGENDA_CONFLICT]: 'text-amber-500',
  [CandidateFollowUpActionType.GENERAL_NOTE]: 'text-neutral-500',
};

export interface CandidateFollowUpAction {
  id: number;
  applicationId: number;
  actionType: CandidateFollowUpActionType;
  actionDate: string;
  remarks?: string | null;
  createdByUserId?: string;
  archived: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateFollowUpActionDto {
  applicationId: number;
  actionType: CandidateFollowUpActionType;
  actionDate?: string;
  remarks?: string;
  createdByUserId?: string;
}

// --- Tag Types ---

export enum CandidateTagCategory {
  POSITIVE = 'positive',
  CONCERN = 'concern',
}

export enum PositiveTagKey {
  GOOD_IMPRESSION_VISIT = 'good_impression_visit',
  STABLE_FAMILY = 'stable_family',
  PAYSLIPS_OK = 'payslips_ok',
  GOOD_REFERENCES = 'good_references',
  IMMEDIATELY_AVAILABLE = 'immediately_available',
  OTHER = 'other',
}

export enum ConcernTagKey {
  ONLY_OCMW_INCOME = 'only_ocmw_income',
  SMOKER = 'smoker',
  NO_FIXED_INCOME = 'no_fixed_income',
  PETS = 'pets',
  MANY_RESIDENTS = 'many_residents',
  DOCUMENTS_NOT_ON_TIME = 'documents_not_on_time',
  OTHER = 'other',
}

export const POSITIVE_TAG_LABELS: Record<PositiveTagKey, string> = {
  [PositiveTagKey.GOOD_IMPRESSION_VISIT]: 'Kwam goed over tijdens bezoek',
  [PositiveTagKey.STABLE_FAMILY]: 'Stabiel gezin',
  [PositiveTagKey.PAYSLIPS_OK]: 'Loonfiches OK',
  [PositiveTagKey.GOOD_REFERENCES]: 'Goede referenties',
  [PositiveTagKey.IMMEDIATELY_AVAILABLE]: 'Onmiddellijk beschikbaar',
  [PositiveTagKey.OTHER]: 'Andere',
};

export const CONCERN_TAG_LABELS: Record<ConcernTagKey, string> = {
  [ConcernTagKey.ONLY_OCMW_INCOME]: 'Alleen inkomen OCMW',
  [ConcernTagKey.SMOKER]: 'Rookt',
  [ConcernTagKey.NO_FIXED_INCOME]: 'Geen vast inkomen',
  [ConcernTagKey.PETS]: 'Huisdieren',
  [ConcernTagKey.MANY_RESIDENTS]: 'Veel bewoners',
  [ConcernTagKey.DOCUMENTS_NOT_ON_TIME]: 'Documenten niet tijdig',
  [ConcernTagKey.OTHER]: 'Andere',
};

export interface CandidateTag {
  id: number;
  applicationId: number;
  category: CandidateTagCategory;
  tagKey: string;
  customText?: string | null;
  createdByUserId?: string;
  createdAt: string;
}

export interface SetTagsDto {
  applicationId: number;
  tags: {
    category: CandidateTagCategory;
    tagKey: string;
    customText?: string;
  }[];
  createdByUserId?: string;
}

// --- Rating ---

export const RATING_LABELS: Record<number, string> = {
  1: 'Slecht',
  2: 'Twijfelachtig',
  3: 'OK',
  4: 'Zeer goed',
  5: 'Uitstekend (nu tekenen!)',
};

// --- Follow-Up Summary (batch endpoint) ---

export interface FollowUpSummary {
  applicationId: number;
  rating: number | null;
  actionCount: number;
  positiveTagCount: number;
  concernTagCount: number;
  actionTypes: CandidateFollowUpActionType[];
  positiveTags?: string[];
  concernTags?: string[];
}
